import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Popular articles widget — surfaces the most-read blog posts by view count.
 *
 * Renders nothing (returns null) if the API fails or yields no items so the
 * page never shows a "loading forever" ghost when the blog module is empty.
 *
 * Two visual variants:
 *   • variant="home"  — full section with heading + CTA, used on the landing
 *   • variant="index" — heading only, used on /blog above the topic grid
 *
 * The card layout is intentionally simpler than Blog.js's PostCard because
 * this widget appears alongside other content types (properties, categories)
 * and needs to stay visually restrained.
 */
const PopularArticles = ({ variant = 'home', limit = 3 }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();
  const isNL = language === 'nl';

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/blog/popular?limit=${limit}`)
      .then(r => (r.ok ? r.json() : []))
      .then(data => {
        if (!cancelled) {
          setPosts(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) return null;
  if (posts.length === 0) return null;

  const titleOf = (p) => (!isNL && p.title_en) ? p.title_en : p.title;
  const excerptOf = (p) => (!isNL && p.excerpt_en) ? p.excerpt_en : p.excerpt;

  const heading = isNL ? 'Populaire artikelen' : 'Popular articles';
  const subhead = isNL
    ? 'De meest gelezen gidsen uit onze Kennisbank'
    : 'The most-read guides from our Knowledge Base';

  return (
    <section
      style={variant === 'home' ? styles.homeSection : styles.indexSection}
      data-testid="popular-articles-section"
    >
      <div style={variant === 'home' ? styles.homeContainer : styles.indexContainer}>
        {variant === 'home' ? (
          <div style={styles.header}>
            <h2 style={styles.homeTitle} data-testid="popular-articles-title">
              <TrendingUp size={22} color="#C5A059" style={{ verticalAlign: 'middle', marginRight: '0.6rem' }} />
              {heading}
            </h2>
            <p style={styles.subhead}>{subhead}</p>
          </div>
        ) : (
          <h2 style={styles.indexTitle} data-testid="popular-articles-title">
            <TrendingUp size={20} color="#C5A059" style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
            {heading}
          </h2>
        )}

        <div style={styles.grid}>
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              style={styles.card}
              data-testid={`popular-article-${p.slug}`}
            >
              <div style={styles.imageWrap}>
                <img
                  src={p.cover_image}
                  alt={titleOf(p)}
                  style={styles.image}
                  loading="lazy"
                />
                {p.views > 0 && (
                  <span style={styles.viewsBadge} data-testid="views-badge">
                    <TrendingUp size={12} /> {p.views.toLocaleString(isNL ? 'nl-NL' : 'en-GB')}
                  </span>
                )}
              </div>
              <div style={styles.body}>
                <h3 style={styles.title} data-testid="popular-article-title">{titleOf(p)}</h3>
                <p style={styles.excerpt}>{excerptOf(p)}</p>
                <div style={styles.meta}>
                  {p.reading_time > 0 && (
                    <span style={styles.metaChip}>
                      <Clock size={13} /> {p.reading_time} {isNL ? 'min' : 'min'}
                    </span>
                  )}
                  <span style={styles.readMore}>
                    {t('blog.readMore') || (isNL ? 'Lees verder' : 'Read more')}
                    <ArrowRight size={14} style={{ marginLeft: '0.3rem' }} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {variant === 'home' && (
          <div style={styles.cta}>
            <Link to="/blog" style={styles.ctaLink} data-testid="popular-articles-cta">
              {isNL ? 'Ga naar Kennisbank' : 'Visit Knowledge Base'}
              <ArrowRight size={16} style={{ marginLeft: '0.4rem' }} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const styles = {
  homeSection: {
    padding: '5rem 0',
    backgroundColor: '#FAF9F6',
  },
  homeContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 1.5rem',
  },
  indexSection: {
    marginBottom: '3.5rem',
  },
  indexContainer: {},
  header: {
    textAlign: 'center',
    marginBottom: '3rem',
  },
  homeTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    color: '#1A1918',
    marginBottom: '0.75rem',
    fontWeight: 500,
  },
  indexTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.6rem',
    color: '#1A1918',
    marginBottom: '1.5rem',
    fontWeight: 500,
  },
  subhead: {
    color: '#8C8B88',
    fontSize: '1.05rem',
    fontFamily: "'Manrope', sans-serif",
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.75rem',
  },
  card: {
    display: 'block',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  },
  imageWrap: {
    position: 'relative',
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    backgroundColor: '#F4EEE1',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  viewsBadge: {
    position: 'absolute',
    top: '0.85rem',
    right: '0.85rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    backgroundColor: 'rgba(26, 25, 24, 0.85)',
    color: '#C5A059',
    padding: '0.3rem 0.65rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    fontFamily: "'Manrope', sans-serif",
    borderRadius: '2px',
    backdropFilter: 'blur(4px)',
  },
  body: {
    padding: '1.35rem 1.4rem 1.5rem',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.2rem',
    color: '#1A1918',
    marginBottom: '0.65rem',
    lineHeight: 1.3,
    fontWeight: 500,
  },
  excerpt: {
    color: '#585755',
    fontSize: '0.88rem',
    lineHeight: 1.6,
    marginBottom: '1rem',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    fontFamily: "'Manrope', sans-serif",
  },
  metaChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    color: '#8C8B88',
    fontSize: '0.78rem',
  },
  readMore: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#C5A059',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
  },
  cta: {
    textAlign: 'center',
    marginTop: '3rem',
  },
  ctaLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#1A1918',
    fontFamily: "'Manrope', sans-serif",
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    borderBottom: '2px solid #C5A059',
    paddingBottom: '0.35rem',
    transition: 'color 0.2s ease',
  },
};

export { PopularArticles };
export default PopularArticles;
